import {useQueryClient} from '@github-ui/react-query'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useAlive} from '@github-ui/use-alive'
import {useDebounce} from '@github-ui/use-debounce'
import {memo, useCallback, useEffect, useRef} from 'react'

import {issuesDashboardSurfaceContentRoute} from '../routes/issues-dashboard-surface-content-route'
import {rowNodeKey, rowNodeQueryKey} from './issues-dashboard-row-node'

// Rate-limit per-row refreshes: collapse a burst of events on one row into a
// single (trailing-edge) invalidation. `maxWait` keeps a refresh landing during
// a sustained stream instead of starving. Trailing-only (no leading) avoids the
// leading+trailing pair re-fetching the same row twice per burst.
const THROTTLE_MS = 2000

type AliveEventData = {wait?: number}

/**
 * Renders render-null subscribers — one per visible row (issue or PR) that
 * carries a signed `aliveChannel` — that invalidate ONLY that row's node query
 * on a real change signal. The node query (see use-enriched-issues) then
 * refetches a single row via GET /issues/_rows, so a live update
 * refreshes just the changed row instead of refetching the whole list. Row-node
 * fetches are coalesced into one batched request by the row-node loader.
 *
 * When the `issues_dashboard_row_live_updates` flag is off the backend omits
 * `aliveChannel`, so this hook subscribes to nothing and is a no-op.
 */
export function useIssuesDashboardLiveUpdates(): React.ReactNode {
  const main = useSuspenseRouteQuery(issuesDashboardSurfaceContentRoute, 'mainQuery')

  return (
    <>
      {main.data.results.map(row => {
        if (!row.aliveChannel) return null
        const key = rowNodeKey(row)
        return <AliveInvalidator key={key} rowKey={key} channel={row.aliveChannel} />
      })}
    </>
  )
}

// Memoized: props are stable per row, so an unrelated parent re-render (deferred
// data landing, state changes) must not re-invoke every visible row's subscriber.
function AliveInvalidatorBase({rowKey, channel}: {rowKey: string; channel: string}) {
  const queryClient = useQueryClient()

  // Invalidate only this row's node query. The key is row-scoped (not
  // route-scoped), so a delayed/queued invalidation can never refresh the wrong
  // view after a filter/page change.
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({queryKey: rowNodeQueryKey(rowKey)})
  }, [queryClient, rowKey])

  const debouncedInvalidate = useDebounce(invalidate, THROTTLE_MS, {
    leading: false,
    maxWait: THROTTLE_MS,
    onChangeBehavior: 'cancel',
  })

  // Track the replication-lag timers so they can be cleared on unmount (the
  // debounce itself is cancelled by `onChangeBehavior: 'cancel'`).
  const waitTimersRef = useRef(new Set<number>())
  useEffect(() => {
    const timers = waitTimersRef.current
    return () => {
      for (const id of timers) window.clearTimeout(id)
      timers.clear()
    }
  }, [])

  // Honor server-provided `wait` (replication-lag hint) before invalidating so
  // the refetch actually sees the new data past replication lag.
  const onEvent = useCallback(
    (data: AliveEventData) => {
      const id = window.setTimeout(() => {
        waitTimersRef.current.delete(id)
        debouncedInvalidate()
      }, data.wait || 0)
      waitTimersRef.current.add(id)
    },
    [debouncedInvalidate],
  )

  useAlive(channel, onEvent)
  return null
}
const AliveInvalidator = memo(AliveInvalidatorBase)
